/**
 * The enquiry form.
 *
 * In the design this was a mock: submitting flipped a boolean and showed the
 * thank-you panel. Here it posts to the Lambda, which relays through Mailgun,
 * and only shows that panel once the message is actually accepted for delivery.
 *
 * Field values live in a plain object owned by this module rather than in the
 * DOM alone, so switching language mid-typing re-renders the labels without
 * throwing away what the visitor has written.
 */

import { h, mount } from '../lib/dom.js';
import { CONTENT, subjectOptions } from '../content.js';
import { sendEnquiry, EnquiryError } from '../lib/api.js';

/** Mirrors the server's limits in api/lib/validate.js — the server is authoritative. */
const LIMITS = { name: 120, email: 254, message: 5000 };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const state = {
  values: { name: '', email: '', subject: 'Recording Production', message: '' },
  errors: {},
  status: 'idle', // idle | sending | sent | error
  formError: null,
  /** When the form was first rendered: a submit under a second is not a human. */
  renderedAt: Date.now(),
  /** Honeypot. A real visitor never sees this field, so a value means a bot. */
  trap: '',
};

export function renderForm(lang) {
  const container = h('div', { class: 'enquiry-host' });
  paint(container, lang);
  return container;
}

function paint(container, lang) {
  mount(container, state.status === 'sent' ? sentPanel(lang) : formEl(container, lang));
}

function sentPanel(lang) {
  const t = CONTENT[lang].write;
  return h(
    'div',
    { class: 'sent-panel', role: 'status', 'aria-live': 'polite' },
    h('h3', null, t.thanks),
    h('p', null, t.thanksSub),
  );
}

function formEl(container, lang) {
  const t = CONTENT[lang].write;
  const busy = state.status === 'sending';

  const field = (key, label, control) =>
    h(
      'div',
      { class: 'field' },
      h('label', { for: `veni-${key}` }, label),
      control,
      state.errors[key] && h('p', { class: 'field-error', id: `veni-${key}-error` }, state.errors[key]),
    );

  const bind = (key) => ({
    id: `veni-${key}`,
    class: 'input',
    value: state.values[key],
    disabled: busy,
    'aria-invalid': state.errors[key] ? 'true' : null,
    'aria-describedby': state.errors[key] ? `veni-${key}-error` : null,
    onInput: (e) => {
      state.values[key] = e.target.value;
      if (state.errors[key]) {
        delete state.errors[key];
        e.target.removeAttribute('aria-invalid');
        const note = document.getElementById(`veni-${key}-error`);
        if (note) note.remove();
      }
    },
  });

  const nameInput = h('input', { ...bind('name'), type: 'text', maxlength: LIMITS.name, placeholder: t.namePh, autocomplete: 'name' });
  const emailInput = h('input', { ...bind('email'), type: 'email', maxlength: LIMITS.email, placeholder: 'you@example.com', autocomplete: 'email' });
  const messageInput = h('textarea', { ...bind('message'), rows: 5, maxlength: LIMITS.message, placeholder: t.messagePh });
  messageInput.value = state.values.message;

  const select = h(
    'select',
    { ...bind('subject'), name: 'subject' },
    subjectOptions(lang).map((o) => h('option', { value: o.value, selected: o.value === state.values.subject }, o.label)),
  );

  return h(
    'form',
    {
      class: 'enquiry',
      novalidate: true,
      onSubmit: (e) => {
        e.preventDefault();
        submit(container, lang);
      },
    },
    h('div', { class: 'row' }, field('name', t.name, nameInput), field('email', t.email, emailInput)),
    field('subject', t.enquiry, select),
    field('message', t.message, messageInput),

    // Honeypot — hidden from sighted visitors and from assistive tech alike.
    h(
      'div',
      { class: 'hp', 'aria-hidden': 'true' },
      h('label', { for: 'veni-website' }, 'Website'),
      h('input', {
        id: 'veni-website',
        name: 'website',
        type: 'text',
        tabindex: '-1',
        autocomplete: 'off',
        value: state.trap,
        onInput: (e) => {
          state.trap = e.target.value;
        },
      }),
    ),

    h(
      'div',
      { class: 'submit-row' },
      h('button', { type: 'submit', class: 'btn btn-primary', disabled: busy }, busy ? t.sending : t.send),
      state.formError && h('p', { class: 'form-error', role: 'alert' }, state.formError),
    ),
  );
}

function validate(lang) {
  const t = CONTENT[lang].write;
  const errors = {};
  const v = state.values;

  if (!v.name.trim()) errors.name = t.errRequired;
  else if (v.name.length > LIMITS.name) errors.name = t.errLong;

  if (!v.email.trim()) errors.email = t.errRequired;
  else if (!EMAIL_RE.test(v.email.trim()) || v.email.length > LIMITS.email) errors.email = t.errEmail;

  if (!v.message.trim()) errors.message = t.errRequired;
  else if (v.message.length > LIMITS.message) errors.message = t.errLong;

  return errors;
}

async function submit(container, lang) {
  const t = CONTENT[lang].write;
  state.errors = validate(lang);
  state.formError = null;

  if (Object.keys(state.errors).length) {
    paint(container, lang);
    container.querySelector('[aria-invalid="true"]')?.focus();
    return;
  }

  state.status = 'sending';
  paint(container, lang);

  try {
    await sendEnquiry({
      name: state.values.name.trim(),
      email: state.values.email.trim(),
      subject: state.values.subject,
      message: state.values.message.trim(),
      lang,
      website: state.trap,
      elapsedMs: Date.now() - state.renderedAt,
    });
    state.status = 'sent';
    paint(container, lang);
    container.querySelector('.sent-panel')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  } catch (err) {
    state.status = 'error';
    // A rejected field comes back named; anything else is a general failure.
    const FIELD_MESSAGE = { name: t.errRequired, email: t.errEmail, message: t.errRequired };
    if (err instanceof EnquiryError && err.field && FIELD_MESSAGE[err.field]) {
      state.errors[err.field] = FIELD_MESSAGE[err.field];
    }
    state.formError = t.errFailed;
    paint(container, lang);
    container.querySelector('.form-error')?.focus?.();
  }
}
